import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axiosClient from '../../axios'
import { getSavedAuthData } from '../../axios/interceptor'

// Types
export interface CreditBalance {
  totalCredits: number
  usedCredits: number
  remainingCredits: number
  periodStartDate: string
  periodEndDate: string
  plan: string
}

export interface CreditUsageItem {
  id: string
  userId: string
  creditsUsed: number
  action: string
  resourceId?: string
  resourceType?: string
  metadata?: Record<string, any>
  createdAt: string
}

export interface CreditUsageHistory {
  history: CreditUsageItem[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

export interface CreditStats {
  [action: string]: {
    count: number
    totalCredits: number
  }
}

// Hooks
export const useCreditBalance = () => {
  const { userId } = getSavedAuthData()

  return useQuery({
    queryKey: ['creditBalance', userId],
    queryFn: async () => {
      const { data } = await axiosClient.get<{ balance: CreditBalance }>('/credits/balance')
      return data
    },
    staleTime: 0, // Always refetch after invalidation
    enabled: !!userId, // Only run if user is logged in
  })
}

export const useCreditHistory = (params: {
  limit?: number
  offset?: number
  startDate?: string
  endDate?: string
} = {}) => {
  const { userId } = getSavedAuthData()

  return useQuery({
    queryKey: ['creditHistory', userId, params],
    queryFn: async () => {
      const { data} = await axiosClient.get<CreditUsageHistory>('/credits/history', { params })
      return data
    },
    staleTime: 1000 * 60, // 1 minute
    enabled: !!userId,
  })
}

export const useCreditStats = (params: {
  startDate?: string
  endDate?: string
} = {}) => {
  const { userId } = getSavedAuthData()

  return useQuery({
    queryKey: ['creditStats', userId, params],
    queryFn: async () => {
      const { data } = await axiosClient.get<{ stats: CreditStats }>('/credits/stats', { params })
      return data
    },
    staleTime: 1000 * 60, // 1 minute
    enabled: !!userId,
  })
}

export const useConsumeCredits = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      amount?: number
      action: string
      resourceId?: string
      resourceType?: string
      metadata?: Record<string, any>
    }) => {
      const { data } = await axiosClient.post<{
        success: boolean
        message: string
        remainingCredits: number
      }>('/credits/consume', payload)
      return data
    },
    onSuccess: () => {
      // Invalidate and refetch credit balance
      queryClient.invalidateQueries({ queryKey: ['creditBalance'] })
      queryClient.invalidateQueries({ queryKey: ['creditHistory'] })
      queryClient.invalidateQueries({ queryKey: ['creditStats'] })
    },
  })
}

export const useCheckCredits = () => {
  return useMutation({
    mutationFn: async (payload: { amount?: number }) => {
      const { data } = await axiosClient.post<{
        success: boolean
        hasEnoughCredits: boolean
        requiredCredits: number
        remainingCredits: number
      }>('/credits/check', payload)
      return data
    },
  })
}
